import React from 'react';
import { MapPin, Navigation, Clock, ShieldCheck, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CoverageAreas: React.FC = () => {
  const { t } = useTranslation();
  const isEng = t('common.loading') === 'Loading...';

  const areas = [
    {
      municipality: 'Talatona',
      hubs: 'Pólo Empresarial, Belas Shopping, Benfica, Samba',
      eta: '10–20 min',
      badge: 'Hub Empresarial'
    },
    {
      municipality: 'Ingombota / Centro',
      hubs: 'Mutamba, Coqueiros, Maculusso, Marginal, Cruzeiro',
      eta: '15–25 min',
      badge: 'Centro Financeiro'
    },
    {
      municipality: 'Maianga & Alvalade',
      hubs: 'Kinaxixi, Sagrada Família, Bairro Azul, Cassenda',
      eta: '10–20 min',
      badge: 'Área Comercial'
    },
    {
      municipality: 'Kilamba & Nova Vida',
      hubs: 'Centralidade do Kilamba, KK5000, Camama, Nova Vida',
      eta: '15–25 min',
      badge: 'Residencial & Comércio'
    },
    {
      municipality: 'Viana & Via Expressa',
      hubs: 'Pólo Industrial, Estrada de Catete, Vila de Viana, Zango',
      eta: '20–30 min',
      badge: 'Logística Industrial'
    },
    {
      municipality: 'Cacuaco & Cazenga',
      hubs: 'Kikolo, Vidrul, Hoji-ya-Henda, São Paulo',
      eta: '20–30 min',
      badge: 'Comércio Geral'
    }
  ];

  return (
    <section id="cobertura" className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden border-t border-gray-100">
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100 text-[#FF6B35] text-xs font-bold uppercase tracking-wider mb-4">
            <Navigation size={14} />
            <span>{isEng ? 'Coverage in Luanda' : 'Cobertura em Toda a Província de Luanda'}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
            {isEng ? 'Fast Deliveries & Couriers in Key Luanda Zones' : 'Entregas Rápidas e Estafetas nas Principais Zonas de Luanda'}
          </h2>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            {isEng
              ? 'Our certified motorcycle fleet operates across all main municipalities of Luanda with GPS tracking and guaranteed arrival times.'
              : 'A nossa frota certificada de estafetas opera nos principais pólos comerciais e residenciais de Luanda, garantindo recolhas ágeis e rastreamento em tempo real.'}
          </p>
        </div>

        {/* Areas Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {areas.map((area, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center text-[#FF6B35]">
                      <MapPin size={18} />
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg">{area.municipality}</h3>
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-blue-50 text-[#2C5282] border border-blue-100">
                    {area.badge}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <strong className="text-gray-700 font-semibold">{isEng ? 'Hubs: ' : 'Pólos: '}</strong>
                  {area.hubs}
                </p>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-xs">
                <span className="flex items-center gap-1.5 text-gray-500 font-medium">
                  <Clock size={14} className="text-green-600" />
                  <span>{isEng ? 'Est. Pickup: ' : 'Recolha média: '}<strong>{area.eta}</strong></span>
                </span>
                <span className="flex items-center gap-1 text-orange-600 font-semibold">
                  <Zap size={14} />
                  <span>SLA Express</span>
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Banner */}
        <div className="mt-12 max-w-4xl mx-auto bg-gradient-to-r from-slate-900 to-[#1e3a8a] text-white rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
          <div className="flex items-center gap-4 text-left">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-[#FFE4B5] shrink-0">
              <ShieldCheck size={28} />
            </div>
            <div>
              <h4 className="font-bold text-base md:text-lg">
                {isEng ? 'Need an urgent courier in Luanda right now?' : 'Precisa de um estafeta urgente em Luanda agora?'}
              </h4>
              <p className="text-sm text-gray-300">
                {isEng
                  ? 'Request instantly via WhatsApp or speak with our corporate dispatch manager.'
                  : 'Faça o seu pedido pelo WhatsApp oficial ou solicite uma frota dedicada para a sua empresa.'}
              </p>
            </div>
          </div>
          <a
            href="https://wa.me/244954009112"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-sm transition-all duration-300 shadow-md hover:shadow-green-500/20 whitespace-nowrap shrink-0"
          >
            {isEng ? 'Call Courier on WhatsApp' : 'Chamar Estafeta no WhatsApp'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default CoverageAreas;
